import { Box, Grid, Stack, styled } from '@mui/material'
import { type GridPaginationModel } from '@mui/x-data-grid'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import NoPatronsCheckedInIcon from 'assets/NoPatronsCheckedIn.png'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { PatronListTable } from './PatronListTable'
import { useGetPatronListByLeCheckins } from 'hooks/api/Reports/useGetPatronListByLeCheckins'
import { useGetRewardsByLicensedEstablishmentId } from 'hooks/api/useGetRewardsByLicensedEstablishmentId'
import { PlayerPaybackReportsHeader } from '../PlayerPaybackReportsHeader'
import { useState, useEffect } from 'react'
import { useSetLe } from 'stores/useSetLe'
import { DatePicker } from '@mui/x-date-pickers'
import { colorPrimitives, Colors } from 'components/Theme'
import { isValid } from 'date-fns'
import { SectionHeader } from 'components/SectionHeader'
import { getLEIdsByPermission } from 'utils/permissions'
import { useSetMe } from 'stores/useMeQuery'

const StyledDatePicker = styled(DatePicker)({
  '& .MuiFormLabel-root': {
    fontSize: '1rem',
    fontWeight: '100',
    color: Colors.muiLabels.placeholder,
  },
  '& .Mui-focused': {
    color: !Colors.muiLabels.placeholder,
  },
})

const getDefaultStartDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 30)
  date.setHours(0, 0, 0, 0) // set to midnight 30 days ago
  return date
}

const getDefaultEndDate = () => {
  const date = new Date()
  date.setHours(23, 59, 59, 999)
  return date
}

export const PatronListPage = () => {
  const { passedLe } = useSetLe()
  const { passedMe } = useSetMe()
  const meQuery = passedMe

  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const licensedEstablishments = meQuery.data?.licensedEstablishments?.filter(
    (le) => le.corporateAccountId === selectedCorporateAccountId
  )

  const leData =
    meQuery?.data?.user?.licensedEstablishments?.find(
      (x) =>
        x.licensedEstablishmentId === Number(passedLe?.id) &&
        meQuery?.data?.licensedEstablishments?.find(
          (x) => x.corporateAccountId === selectedCorporateAccountId
        )?.id
    ) ?? undefined

  const piiPermission = leData?.reportsAccessToPIIPermission
  const downloadPIIPermission = leData?.reportsDownloadPIIPermission

  const [startDate, setStartDate] = useState<Date>(getDefaultStartDate())
  const [endDate, setEndDate] = useState<Date>(getDefaultEndDate())
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  })

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize })
  }, [startDate, endDate])

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model)
  }

  // Update query params when `id` changes
  const reportQuery = useGetPatronListByLeCheckins({
    paginationModel,
    currentPage: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    licensedEstablishmentId: Number(passedLe?.id),
    startDate,
    endDate,
  })

  const leRewardsQuery = useGetRewardsByLicensedEstablishmentId({
    licensedEstablishmentId: passedLe?.id,
  })

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'playerPaybackReportsPermission'
  )

  return (
    <PlayerPaybackReportsHeader currentTab={'2'}>
      {!licensedEstablishments || licensedEstablishments.length === 0 ? (
        <NoDataIndicator
          imageSource={NoPatronsCheckedInIcon}
          noDataMessage="Cannot view reports because there are no locations available for this corporate account"
        />
      ) : (
        leIdsByPermission.length !== 0 && (
          <Stack spacing={4}>
            <SectionHeader title="Patrons" showButton={false} />
            <Grid
              container
              className="flex flex-column"
              rowGap={2}
              columnGap={3}
            >
              <StyledDatePicker
                disabled={Number(passedLe?.id) === 0}
                disableFuture
                onChange={(date: any) => {
                  if (isValid(date)) {
                    setStartDate(date.setHours(0, 0, 0, 0))
                  }
                }}
                label="Start Date"
                value={startDate}
                format="MM/dd/yyyy"
              />

              <StyledDatePicker
                disabled={Number(passedLe?.id) === 0}
                onChange={(date: any) => {
                  if (isValid(date)) {
                    setEndDate(date.setHours(23, 59, 59, 0))
                  }
                }}
                label="End Date"
                value={endDate}
                format="MM/dd/yyyy"
              />
            </Grid>
            {startDate && endDate && startDate > endDate && (
              <ErrorIndicator
                color={colorPrimitives.darkRed}
                message={'Start Date must be before End Date'}
              />
            )}

            {reportQuery.data?.results &&
              reportQuery.data.results?.length > 0 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box flex="1" textAlign="right">
                    <Stack>
                      {`Total Check-ins: ${
                        reportQuery.data?.totals?.CheckinTotal ?? 0
                      }`}
                    </Stack>
                    {leRewardsQuery.data?.some(
                      (r) => r.type === 'LicensedEstablishment'
                    ) && (
                      <Stack>
                        {`Total Location Points: ${
                          reportQuery.data?.totals?.LocationPointsTotal ?? 0
                        }`}
                      </Stack>
                    )}
                    {leRewardsQuery.data?.some(
                      (r) => r.type === 'Organization'
                    ) && (
                      <Stack>
                        {`Total Organization Points: ${
                          reportQuery.data?.totals?.OrganizationPointsTotal ?? 0
                        }`}
                      </Stack>
                    )}
                    {leRewardsQuery.data?.some(
                      (r) => r.type === 'CorporateAccount'
                    ) && (
                      <Stack>
                        {`Total Corporate Points: ${
                          reportQuery.data?.totals
                            ?.CorporateAccountPointsTotal ?? 0
                        }`}
                      </Stack>
                    )}
                  </Box>
                </Box>
              )}

            <PatronListTable
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationModelChange}
              piiPermission={piiPermission ?? false}
              downloadPIIPermission={downloadPIIPermission ?? false}
              startDate={startDate}
              endDate={endDate}
              showCorporateAccountRewards={
                leRewardsQuery.data?.some(
                  (r) => r.type === 'CorporateAccount'
                ) ?? false
              }
              showOrgRewards={
                leRewardsQuery.data?.some((r) => r.type === 'Organization') ??
                false
              }
              showLeRewards={
                leRewardsQuery.data?.some(
                  (r) => r.type === 'LicensedEstablishment'
                ) ?? false
              }
            />
          </Stack>
        )
      )}
    </PlayerPaybackReportsHeader>
  )
}
